import * as React from 'react';

import { MAX_NUM_OF_LIKES } from '@/constants';
import useDebouncedEffect from '@/hooks/use-debounced-effect';
import type { ContentData } from '@/types/content.types';

import type { Metrics } from './ContentProvider.types';

const PERSIST_IN_DEV = true;

export function useBlogPostMetrics(contentData: ContentData) {
  const { slug, category, contentType } = contentData;
  const categorySlug = category.slug;

  const [metrics, setMetrics] = React.useState<Metrics>({
    hits: null,
    totalNumLikes: null,
    totalNumLikesFromUser: null,
    lastInteractionAt: 0,
  });

  const metricsRef = React.useRef(metrics);
  metricsRef.current = metrics;

  React.useEffect(() => {
    // We don’t track metrics for snippets.
    if (contentType === 'snippet') {
      return;
    }

    fetch(
      `/api/get-article-metrics?slug=${slug}&categorySlug=${categorySlug}`
    )
      .then((res) => res.json())
      .then(
        ({ error, hits, totalNumLikes, totalNumLikesFromUser }) => {
          if (
            error ||
            typeof hits !== 'number' ||
            typeof totalNumLikes !== 'number' ||
            typeof totalNumLikesFromUser !== 'number'
          ) {
            console.error('Could not fetch article metrics');
            return;
          }

          setMetrics((currentMetrics) => ({
            ...currentMetrics,
            hits,
            totalNumLikes,
            totalNumLikesFromUser,
          }));
        }
      );
  }, [contentType, slug, categorySlug]);

  // Whenever the like button is incremented/decremented, we’ll fire off a fetch request to update the DB, but we debounce it so that we don’t spam the server when the user clicks quickly.
  useDebouncedEffect(
    () => {
      // Ignore the effect that runs when we first load all of the metrics.
      if (
        typeof metrics.totalNumLikesFromUser !== 'number' ||
        metricsRef.current.lastInteractionAt === 0
      ) {
        return;
      }

      if (process.env.NODE_ENV === 'production' || PERSIST_IN_DEV) {
        fetch('/api/toggle-like-button/', {
          method: 'POST',
          body: JSON.stringify({
            slug,
            categorySlug,
            numLikes: metrics.totalNumLikesFromUser,
          }),
        });
      }
    },
    [slug, categorySlug, metrics.totalNumLikesFromUser],
    500
  );

  const toggleLikeButton = React.useCallback(
    (action: 'increment' | 'decrement') => {
      const metrics = metricsRef.current;

      if (
        typeof metrics.totalNumLikes !== 'number' ||
        typeof metrics.totalNumLikesFromUser !== 'number'
      ) {
        throw new Error(
          'Tried to toggle like button before we have any metrics! This shouldn’t happen, since we shouldn’t show the like button until the metrics have been fetched.'
        );
      }

      let incrementBy = 0;
      if (
        action === 'increment' &&
        metrics.totalNumLikesFromUser < MAX_NUM_OF_LIKES
      ) {
        incrementBy = 1;
      } else if (
        action === 'decrement' &&
        metrics.totalNumLikesFromUser > 0
      ) {
        incrementBy = -1;
      }

      const newTotalNumLikes = metrics.totalNumLikes + incrementBy;
      const newTotalNumLikesFromUser =
        metrics.totalNumLikesFromUser + incrementBy;

      setMetrics({
        ...metricsRef.current,
        totalNumLikes: newTotalNumLikes,
        totalNumLikesFromUser: newTotalNumLikesFromUser,
        lastInteractionAt: Date.now(),
      });

      return newTotalNumLikesFromUser;
    },
    []
  );

  return { metrics, actions: { toggleLikeButton } };
}
